import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  if (!loading && user) return <Navigate to="/app/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await login(email, password);
      toast.success("Welcome home.");
      navigate("/app/dashboard");
    } catch (e) {
      const msg = apiErrorMessage(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="login-page">
      <div className="hidden md:block relative bg-[#1B1533]">
        <div className="absolute inset-0 flex items-center justify-center">
          <svg viewBox="0 0 400 400" className="w-2/3 h-auto opacity-90">
            <defs><linearGradient id="lgg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#E8552B" /><stop offset="100%" stopColor="#F4A72C" /></linearGradient></defs>
            {[180,140,100].map((r,i) => <circle key={r} cx="200" cy="200" r={r} fill="none" stroke="#E8552B" strokeWidth="1" opacity={0.25 - i*0.05} />)}
            <ellipse cx="200" cy="200" rx="42" ry="60" fill="url(#lgg)" />
          </svg>
        </div>
        <Link to="/" className="absolute top-6 left-6 font-serif text-3xl text-white">Vansha</Link>
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="font-serif text-3xl leading-snug max-w-md italic">The quiet operating system for the most important journey of your life.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 md:p-16">
        <div className="w-full max-w-sm">
          <Link to="/" className="md:hidden font-serif text-3xl text-[#1B1533] block mb-8">Vansha</Link>
          <p className="overline mb-3 text-saffron">Welcome back</p>
          <h1 className="font-serif text-4xl text-[#1B1533] mb-2">Sign in</h1>
          <p className="text-[#3D2E5C] mb-10">Continue your family's story.</p>
          <form onSubmit={onSubmit} className="space-y-8" data-testid="login-form">
            <div>
              <label className="overline mb-2 block">Email</label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="input-line" placeholder="you@home.com" data-testid="login-email"
              />
            </div>
            <div>
              <label className="overline mb-2 block">Password</label>
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="input-line" placeholder="•••••••" data-testid="login-password"
              />
            </div>
            {err && <p className="text-sm text-terracotta" data-testid="login-error">{err}</p>}
            <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="login-submit-button">
              {busy ? "Signing in…" : "Sign in"}
            </button>
            <p className="text-sm text-[#7A6E96] text-center">
              No account yet? <Link to="/register" className="text-terracotta underline-offset-2 hover:underline" data-testid="login-go-register">Begin your journey</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
